import { createLogic } from 'redux-logic';
import { normalize, schema } from 'normalizr';
import { find, findIndex, get, map, pick, sample, toNumber } from 'lodash';
import { hosts } from 'settings';
import { decorateWorkout, decorateWorkoutSteps } from 'decorators';

// Action Types
const CANCEL = '@@app/workout/CANCEL';
const UPDATE_STEP = '@@app/workoutPlayer/UPDATE_STEP';

const FETCH = '@@app/workout/FETCH';
const FETCH_SUCCESS = '@@app/workout/FETCH_SUCCESS';
const FETCH_ERROR = '@@app/workout/FETCH_ERROR';

const FETCH_COLLECTION = '@@app/workout/FETCH_COLLECTION';
const FETCH_COLLECTION_SUCCESS = '@@app/workout/FETCH_COLLECTION_SUCCESS';
const FETCH_COLLECTION_ERROR = '@@app/workout/FETCH_COLLECTION_ERROR';

const FETCH_COLLECTIONS = '@@app/workout/FETCH_COLLECTIONS';
const FETCH_COLLECTIONS_SUCCESS = '@@app/workout/FETCH_COLLECTIONS_SUCCESS';
const FETCH_COLLECTIONS_ERROR = '@@app/workout/FETCH_COLLECTIONS_ERROR';

const FETCH_INSPIRATION = '@@app/workout/FETCH_INSPIRATION';
const FETCH_INSPIRATION_SUCCESS = '@@app/workout/FETCH_INSPIRATION_SUCCESS';
const FETCH_INSPIRATION_ERROR = '@@app/workout/FETCH_INSPIRATION_ERROR';

const FETCH_VIDEOS = '@@app/workout/FETCH_VIDEOS';
const FETCH_VIDEOS_SUCCESS = '@@app/workout/FETCH_VIDEOS_SUCCESS';

const FETCH_VIDEO = '@@app/workout/FETCH_VIDEO';
const FETCH_VIDEO_CURRENT = '@@app/workout/FETCH_VIDEO_CURRENT';
const FETCH_VIDEO_SUCCESS = '@@app/workout/FETCH_VIDEO_SUCCESS';

// Initial State
export const initialState = {
  isLoading: false,
  result: undefined,
  currentStepIndex: 0,
  currentVideoId: null,
  entities: {
    exercises: {},
    workouts: {},
    collections: {},
    videos: {},
    inspiration: [],
    steps: [],
  },
  pagination: {
    videos: {},
    collections: {},
  },
};

// Normalizr
export const normalizeWorkout = data => {
  const exercise = new schema.Entity('exercises');
  const workout = new schema.Entity('workouts', {
    exercises: [exercise],
  });

  return normalize(data, workout);
};

// Observables
const fetchLogic = createLogic({
  type: FETCH,
  cancelType: CANCEL,
  processOptions: {
    successType: FETCH_SUCCESS,
    failType: FETCH_ERROR,
  },
  process({ action, httpClient }) {
    const { exercisePlanId, dayId, stepId } = action.payload;

    return httpClient
      .get(`/v1/voome/exercise-plans/${exercisePlanId}/days/${dayId}`)
      .then(response => ({ ...response, stepId }));
  },
});

const transformFetchSuccessLogic = createLogic({
  type: FETCH_SUCCESS,
  transform({ action }, next) {
    const { data, stepId } = action.payload;
    const payload = normalizeWorkout(data);
    const { entities, result } = payload;
    const workout = decorateWorkout(entities.workouts[result]);

    // Rebuild workouts
    entities.workouts = {
      ...entities.workouts,
      [result]: workout,
    };

    // Add steps for the workout player
    if (stepId !== undefined) {
      entities.steps = decorateWorkoutSteps(workout, entities.exercises);
      const index = findIndex(entities.steps, { id: toNumber(stepId) });

      if (index > -1) {
        payload.currentStepIndex = index;
      }
    }

    next({ ...action, payload: { ...payload, entities } });
  },
});

const fetchCollectionsLogic = createLogic({
  type: FETCH_COLLECTIONS,
  cancelType: CANCEL,
  processOptions: {
    successType: FETCH_COLLECTIONS_SUCCESS,
    failType: FETCH_COLLECTIONS_ERROR,
  },
  process({ action, httpClient }) {
    const { subscriptionId, page } = action.payload;
    const endpoint = `/v1/voome/workout-collections/all-ids`;
    const params = { subscriptionId, page };
    const collection = new schema.Entity('collections');

    return httpClient.get(endpoint, { params }).then(response => {
      const { data, pagination } = response;
      const { entities, result } = normalize(data, [collection]);

      return {
        entities,
        pagination: { [page]: { ...pagination, result } },
      };
    });
  },
});

const fetchCollectionLogic = createLogic({
  type: FETCH_COLLECTION,
  cancelType: CANCEL,
  processOptions: {
    successType: FETCH_COLLECTION_SUCCESS,
    failType: FETCH_ERROR,
  },
  process({ action, httpClient }) {
    const { key, voomeProductId } = action.payload;
    const endpoint = `/v1/voome/workout-collections/${key}`;
    const params = { voomeProductId };

    const workout = new schema.Entity('workouts');
    const collection = new schema.Entity('collections', {
      workouts: [workout],
    });

    return httpClient
      .get(endpoint, { params })
      .then(response => normalize(response.data, collection));
  },
});

const fetchInspirationLogic = createLogic({
  type: FETCH_INSPIRATION,
  cancelType: CANCEL,
  processOptions: {
    successType: FETCH_INSPIRATION_SUCCESS,
    failType: FETCH_INSPIRATION_ERROR,
  },
  process({ action, httpClient }) {
    const workouts = [new schema.Entity('workouts')];
    const endpoint = `/v1/members/${
      action.payload.memberId
    }/voome-profile/fitness-inspo`;

    return httpClient.get(endpoint).then(response => {
      return normalize(map(response.data, decorateWorkout), workouts);
    });
  },
});

const fetchVideosLogic = createLogic({
  type: FETCH_VIDEOS,
  cancelType: CANCEL,
  processOptions: {
    successType: FETCH_VIDEOS_SUCCESS,
    failType: FETCH_ERROR,
  },
  process({ action, httpClient }) {
    const endpoint = '/v1/voome/workout-videos';
    const { params } = action.payload;
    const videoSchema = new schema.Entity('videos');

    const onSuccess = ({ data, pagination }) => ({
      ...normalize(data, [videoSchema]),
      pagination,
    });

    return httpClient.get(endpoint, { params }).then(onSuccess);
  },
});

const fetchVideoLogic = createLogic({
  type: FETCH_VIDEO,
  cancelType: CANCEL,
  processOptions: {
    successType: FETCH_VIDEO_SUCCESS,
    failType: FETCH_ERROR,
  },
  process({ action, httpClient }) {
    const endpoint = `/v1/voome/workout-videos/${action.payload.videoId}`;
    return httpClient.get(endpoint);
  },
});

const fetchVideoCurrentLogic = createLogic({
  type: FETCH_VIDEO_CURRENT,
  cancelType: CANCEL,
  processOptions: {
    successType: FETCH_VIDEO_SUCCESS,
    failType: FETCH_ERROR,
  },
  process({ action, httpClient }) {
    const endpoint = `/v1/voome/workout-videos/current`;
    const params = { voomeProductId: action.payload.voomeProductId };
    const onSuccess = response => ({
      ...response,
      currentVideoId: response.data.id,
    });

    return httpClient.get(endpoint, { params }).then(onSuccess);
  },
});

export const workoutLogic = [
  fetchCollectionsLogic,
  fetchCollectionLogic,
  fetchInspirationLogic,
  fetchLogic,
  fetchVideoLogic,
  fetchVideoCurrentLogic,
  fetchVideosLogic,
  transformFetchSuccessLogic,
];

// Selectors
export const selectWorkouts = state => {
  return state.entities.workouts || {};
};

export const selectWorkout = (state, id) => {
  return selectWorkouts(state)[id || state.result];
};

export const selectWorkoutsByIds = (state, ids) => {
  return map(ids, id => selectWorkout(state, id));
};

export const selectWorkoutCollections = state => {
  return state.entities.collections;
};

export const selectWorkoutCollectionsPagination = (state, page) => {
  const pagination = state.pagination.collections[page];

  return pagination
    ? {
        ...pagination,
        result: pagination.result.map(id => state.entities.collections[id]),
      }
    : {};
};

export const selectWorkoutCollection = (state, hackId) => {
  const collection = selectWorkoutCollections(state)[hackId];

  return collection
    ? {
        ...collection,
        workouts: selectWorkoutsByIds(state, collection.workouts),
      }
    : undefined;
};

export const selectWorkoutInspiration = state => {
  return selectWorkoutsByIds(state, state.entities.inspiration);
};

export const selectWorkoutVideos = (state, page = 1) => {
  const pagination = state.pagination.videos[page];

  return pagination
    ? {
        ...pagination,
        result: map(pagination.result, id => state.entities.videos[id]),
      }
    : {};
};

export const selectWorkoutVideo = (state, videoId) => {
  return state.entities.videos[videoId];
};

export const selectExercises = state => {
  return state.entities.exercises;
};

export const selectWorkoutSteps = state => {
  return state.entities.steps;
};

export const selectCurrentStep = state => {
  return get(selectWorkoutSteps(state), state.currentStepIndex);
};

export const selectPreviousStep = state => {
  return get(selectWorkoutSteps(state), state.currentStepIndex - 1);
};

export const selectNextStep = state => {
  return get(selectWorkoutSteps(state), state.currentStepIndex + 1);
};

// Helpers
export const workoutPrintUri = (dayId, exercisePlanId, subscriptionId) => {
  if (dayId && exercisePlanId && subscriptionId) {
    return [
      hosts.api,
      `v1/voome/exercise-plans/${exercisePlanId}`,
      `days/${dayId}`,
      `print?subscription_id=${subscriptionId}&thumbnails=true`,
    ].join('/');
  }
};

// Action Creators
export const fetchWorkout = (
  exercisePlanId,
  dayId,
  stepId,
  currentStepIndex
) => {
  return {
    type: FETCH,
    payload: { exercisePlanId, dayId, stepId, currentStepIndex },
  };
};

export const fetchWorkoutCollections = (subscriptionId, page) => {
  return { type: FETCH_COLLECTIONS, payload: { subscriptionId, page } };
};

export const fetchWorkoutCollection = (key, voomeProductId) => {
  return { type: FETCH_COLLECTION, payload: { key, voomeProductId } };
};

export const fetchWorkoutInspiration = memberId => {
  return { type: FETCH_INSPIRATION, payload: { memberId } };
};

export const fetchWorkoutVideo = videoId => {
  return { type: FETCH_VIDEO, payload: { videoId } };
};

export const fetchWorkoutVideoCurrent = voomeProductId => {
  return { type: FETCH_VIDEO_CURRENT, payload: { voomeProductId } };
};

export const fetchWorkoutVideos = params => {
  const {
    order = 'our_picks',
    page = 1,
    type,
    equipmentRequired,
    time,
  } = params;

  return {
    type: FETCH_VIDEOS,
    payload: {
      params: {
        order,
        page,
        type,
        equipmentRequired,
        time,
      },
    },
  };
};

export const updateWorkoutStep = index => {
  return { type: UPDATE_STEP, payload: { index } };
};

export const previousWorkoutStep = index => {
  return updateWorkoutStep(index - 1);
};

export const nextWorkoutStep = index => {
  return updateWorkoutStep(index + 1);
};

// Reducer
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH:
    case FETCH_COLLECTIONS:
    case FETCH_COLLECTION:
    case FETCH_INSPIRATION:
    case FETCH_VIDEOS:
    case FETCH_VIDEO:
      return {
        ...state,
        isLoading: true,
      };

    case FETCH_SUCCESS:
      return {
        ...state,
        isLoading: false,
        result: payload.result,
        currentStepIndex: payload.currentStepIndex || 0,
        entities: {
          ...state.entities,
          steps: payload.entities.steps,
          exercises: {
            ...state.entities.exercises,
            ...payload.entities.exercises,
          },
          workouts: {
            ...state.entities.workouts,
            ...payload.entities.workouts,
          },
        },
      };

    case FETCH_COLLECTIONS_SUCCESS:
      return {
        ...state,
        isLoading: false,
        pagination: {
          ...state.pagination,
          collections: {
            ...state.pagination.collections,
            ...payload.pagination,
          },
        },
        entities: {
          ...state.entities,
          collections: {
            ...state.entities.collections,
            ...payload.entities.collections,
          },
        },
      };

    case FETCH_COLLECTION_SUCCESS:
      return {
        ...state,
        isLoading: false,
        result: payload.result,
        entities: {
          ...state.entities,
          collections: {
            ...state.entities.collections,
            ...payload.entities.collections,
          },
          workouts: {
            ...state.entities.workouts,
            ...payload.entities.workouts,
          },
        },
      };

    case FETCH_INSPIRATION_SUCCESS:
      return {
        ...state,
        isLoading: false,
        entities: {
          ...state.entities,
          inspiration: payload.result,
          workouts: {
            ...state.entities.workouts,
            ...payload.entities.workouts,
          },
        },
      };

    case FETCH_VIDEOS_SUCCESS:
      return {
        ...state,
        isLoading: false,
        entities: {
          ...state.entities,
          videos: {
            ...payload.entities.videos,
            ...state.entities.videos,
          },
        },
        pagination: {
          ...state.pagination,
          videos: {
            ...state.pagination.video,
            [payload.pagination.page]: {
              ...payload.pagination,
              result: payload.result,
            },
          },
        },
      };

    case FETCH_VIDEO_SUCCESS:
      return {
        ...state,
        isLoading: false,
        currentVideoId: payload.currentVideoId || state.currentVideoId,
        entities: {
          ...state.entities,
          videos: {
            ...state.entities.videos,
            [payload.data.id]: payload.data,
          },
        },
      };

    case UPDATE_STEP:
      return {
        ...state,
        currentStepIndex: payload.index,
      };

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/logic/workout.js